import { api } from './api';
import {
    EntityDto,
    CreateEntityDto,
    CreateEntityResponse,
    RoleDto,
    AddUserToEntityDto,
    UserSelfAddToEntityDto,
    UserSelfAddToEntityResponse,
    UpdateEntityDto,
} from './dtos/entity';
import { accountApi } from './accountEndpoints';
import { setToken } from 'store/reducers/authSlice';

import { TeamMembersPageDto } from 'api/dtos/teamMember';

type TeamMembersQueryParams = { entityId: string; limit?: number; offset?: number; query?: string };

export const entityApi = api.injectEndpoints({
    endpoints: (builder) => ({
        createEntity: builder.mutation<CreateEntityResponse, CreateEntityDto>({
            query: (body) => ({
                url: 'entity/create',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Entity', 'Account'],
            extraOptions: {
                successMessage: 'Podmiot został pomyślnie utworzony.',
                errorMessage: 'Nie udało się utworzyć podmiotu. Spróbuj ponownie.',
            },
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    const result = await dispatch(accountApi.endpoints.refreshToken.initiate());
                    if ('data' in result && result.data?.token) {
                        dispatch(setToken(result.data.token));
                    }
                } catch {}
            },
        }),
        getEntities: builder.query<EntityDto[], void>({
            query: () => 'entity/all',
            providesTags: ['Entity'],
        }),
        getEntityById: builder.query<EntityDto, string>({
            query: (entityId) => `entity/${entityId}`,
            providesTags: ['Entity'],
        }),
        updateEntity: builder.mutation<string, { entityId: string; body: UpdateEntityDto }>({
            query: ({ entityId, body }) => ({
                url: `entity/${entityId}/update`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Entity', 'Account'],
            extraOptions: {
                successMessage: 'Podmiot został pomyślnie zaktualizowany.',
                errorMessage: 'Nie udało się zaktualizować podmiotu. Spróbuj ponownie.',
            },
        }),
        deleteEntity: builder.mutation<string, string>({
            query: (entityId) => ({
                url: `entity/${entityId}/remove`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Entity', 'Account'],
            extraOptions: {
                successMessage: 'Podmiot został pomyślnie usunięty.',
                errorMessage: 'Nie udało się usunąć podmiotu. Spróbuj ponownie.',
            },
        }),
        updateUserRole: builder.mutation<void, { entityId: string; userId: string; role: RoleDto }>({
            query: ({ entityId, userId, role }) => ({
                url: `entity/${entityId}/users/${userId}/role`,
                method: 'PUT',
                body: role,
            }),
            invalidatesTags: ['Entity', 'TeamMember', 'Account'],
            extraOptions: {
                successMessage: 'Rola użytkownika została pomyślnie zaktualizowana.',
                errorMessage: 'Nie udało się zaktualizować roli użytkownika. Spróbuj ponownie.',
            },
        }),
        userSelfAddToEntity: builder.mutation<UserSelfAddToEntityResponse, UserSelfAddToEntityDto>({
            query: (body) => ({
                url: 'entity/user-self-add',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Entity', 'Account'],
            extraOptions: {
                successMessage: 'Pomyślnie dołączyłeś do podmiotu.',
                errorMessage: 'Nie udało się dołączyć do podmiotu. Sprawdź token zaproszenia i spróbuj ponownie.',
            },
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    const result = await dispatch(accountApi.endpoints.refreshToken.initiate());
                    if ('data' in result && result.data?.token) {
                        dispatch(setToken(result.data.token));
                    }
                } catch {}
            },
        }),
        refreshInvitationToken: builder.mutation<string, string>({
            query: (entityId) => ({
                url: `entity/${entityId}/refresh-invitation-token`,
                method: 'POST',
            }),
            invalidatesTags: ['Entity'],
            extraOptions: {
                successMessage: 'Token zaproszenia został pomyślnie odświeżony.',
                errorMessage: 'Nie udało się odświeżyć tokenu zaproszenia. Spróbuj ponownie.',
            },
        }),
        addUserToEntity: builder.mutation<string, { entityId: string; body: AddUserToEntityDto }>({
            query: ({ entityId, body }) => ({
                url: `entity/${entityId}/add-user`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Entity'],
            extraOptions: {
                successMessage: 'Użytkownik został pomyślnie dodany do podmiotu.',
                errorMessage: 'Nie udało się dodać użytkownika do podmiotu. Spróbuj ponownie.',
            },
        }),
        removeUserFromEntity: builder.mutation<string, { entityId: string; userIdToRemove: string }>({
            query: ({ entityId, userIdToRemove }) => ({
                url: `entity/${entityId}/kick-user/${userIdToRemove}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Entity', 'TeamMember', 'Points'],
            extraOptions: {
                successMessage: 'Użytkownik został pomyślnie usunięty z podmiotu.',
                errorMessage: 'Nie udało się usunąć użytkownika z podmiotu. Spróbuj ponownie.',
            },
        }),
        kickAllNonPrivilegedUsers: builder.mutation<{ removedCount: number }, { entityId: string }>({
            query: ({ entityId }) => ({
                url: `entity/${entityId}/kick-all-non-privileged`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Entity', 'TeamMember', 'Points'],
            extraOptions: {
                successMessage: 'Wszyscy zwykli członkowie zostali usunięci z podmiotu.',
                errorMessage: 'Nie udało się usunąć użytkowników. Spróbuj ponownie.',
            },
        }),
        refreshEntityQr: builder.mutation<void, { entityId: string; }>({
            query: ({ entityId }) => ({
                url: `entity/${entityId}/qr/refresh`,
                method: 'PUT',
            }),
            invalidatesTags: ['Entity'],
            extraOptions: {
                successMessage: 'Kod QR został pomyślnie odświeżony.',
                errorMessage: 'Nie udało się odświeżyć kodu QR. Spróbuj ponownie.',
            },
        }),
        getTeamMembers: builder.query<TeamMembersPageDto, string | TeamMembersQueryParams>({
            query: (args) => (typeof args === 'string'
                ? `entity/${args}/team-members`
                : {
                    url: `entity/${args.entityId}/team-members`,
                    params: {
                        limit: args.limit,
                        offset: args.offset,
                        query: args.query,
                    },
                }),
            providesTags: ['TeamMember'],
        }),
        assignUserTags: builder.mutation<
            void,
            { entityId: string; userId: string; tagIds: string[] }
        >({
            query: ({ entityId, userId, tagIds }) => ({
                url: `entity/${entityId}/assign-tag/user/${userId}`,
                method: 'PUT',
                body: { tagIds },
            }),
            invalidatesTags: ['Entity', 'TeamMember', 'Account'],
            extraOptions: {
                successMessage: 'Tagi zostały pomyślnie przypisane użytkownikowi.',
                errorMessage: 'Nie udało się przypisać tagów użytkownikowi. Spróbuj ponownie.',
            },
        }),
    }),
    overrideExisting: false,
});
export const {
    useCreateEntityMutation,
    useGetEntitiesQuery,
    useGetEntityByIdQuery,
    useUpdateUserRoleMutation,
    useUpdateEntityMutation,
    useDeleteEntityMutation,
    useUserSelfAddToEntityMutation,
    useRefreshInvitationTokenMutation,
    useAddUserToEntityMutation,
    useRemoveUserFromEntityMutation,
    useKickAllNonPrivilegedUsersMutation,
    useGetTeamMembersQuery,
    useAssignUserTagsMutation,
    useRefreshEntityQrMutation,
} = entityApi;
